import React, { useState } from "react";
import { PAIN_MODULE } from "../data/painTeachingData";

function medal(correct, total) {
  const pct = Math.round((correct / total) * 100);
  if (pct >= 85) return { icon: "🥇", label: "Excellent" };
  if (pct >= 60) return { icon: "🥈", label: "Good" };
  return               { icon: "🥉", label: "Keep practising" };
}

const PainTeachingGame = ({ onComplete, userData, saveUserData, module: moduleProp }) => {
  const module = moduleProp ?? PAIN_MODULE;

  const [stepIdx, setStepIdx]           = useState(-1);
  const [selected, setSelected]         = useState(null);
  const [answered, setAnswered]         = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [phase, setPhase]               = useState("intro");

  const questions = module.steps.filter(s => s.type === "question");
  const totalQ    = questions.length;
  const step      = stepIdx >= 0 ? module.steps[stepIdx] : null;
  const isLast    = stepIdx === module.steps.length - 1;

  const startModule = () => { setStepIdx(0); setPhase("steps"); };

  const advance = () => {
    if (isLast) {
      const pct   = Math.round((correctCount / totalQ) * 100);
      const coins = Math.max(5,  Math.floor(pct / 5));
      const xp    = Math.max(15, Math.floor(pct / 2));
      if (saveUserData && userData) {
        saveUserData({ ...userData, coins: (userData.coins || 0) + coins, xp: (userData.xp || 0) + xp });
      }
      setPhase("done");
    } else {
      setStepIdx(i => i + 1);
      setSelected(null);
      setAnswered(false);
    }
  };

  const submitAnswer = () => {
    if (!selected) return;
    if (step.options.find(o => o.id === selected)?.correct) setCorrectCount(c => c + 1);
    setAnswered(true);
  };

  const ProgressBar = () => (
    <div className="px-4 pt-2 pb-1 flex gap-1 flex-shrink-0">
      {module.steps.map((s, i) => (
        <div key={i} className={`h-1.5 rounded-full flex-1 ${
          i < stepIdx  ? (s.type === "question" ? "bg-teal-500" : "bg-emerald-400") :
          i === stepIdx ? "bg-yellow-400" : "bg-gray-200"
        }`} />
      ))}
    </div>
  );

  // ── Intro ─────────────────────────────────────────────────────────────────
  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="text-6xl mb-3">{module.emoji}</div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-1">{module.title}</h2>
            <p className="text-gray-500 text-sm">with {module.npc}</p>
          </div>

          <div className="bg-teal-50 rounded-2xl p-4 mb-6">
            <p className="text-sm text-teal-900 leading-relaxed">{module.intro}</p>
          </div>

          <div className="flex gap-4 text-center mb-6 text-sm text-gray-500">
            <div className="flex-1 bg-gray-50 rounded-xl p-3">
              <div className="text-2xl mb-1">📖</div>
              <div>{module.steps.filter(s => s.type === "teach").length} topics</div>
            </div>
            <div className="flex-1 bg-gray-50 rounded-xl p-3">
              <div className="text-2xl mb-1">❓</div>
              <div>{totalQ} questions</div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onComplete}
              className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-500 font-medium hover:bg-gray-50 transition-colors"
            >
              Not now
            </button>
            <button
              onClick={startModule}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-bold hover:opacity-90 transition-opacity"
            >
              Start →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Done ──────────────────────────────────────────────────────────────────
  if (phase === "done") {
    const m     = medal(correctCount, totalQ);
    const pct   = Math.round((correctCount / totalQ) * 100);
    const coins = Math.max(5,  Math.floor(pct / 5));
    const xp    = Math.max(15, Math.floor(pct / 2));

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="text-6xl mb-2">{m.icon}</div>
            <h2 className="text-2xl font-extrabold text-gray-900">{m.label}</h2>
            <p className="text-gray-500 text-sm mt-1">{module.title} completed</p>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 mb-6 text-center">
            <div className="text-3xl font-extrabold text-teal-700">{correctCount}/{totalQ}</div>
            <div className="text-sm text-teal-600">questions correct</div>
            <div className="flex justify-center gap-6 mt-3">
              <div>
                <div className="font-bold text-yellow-600">+{coins} 🪙</div>
                <div className="text-xs text-gray-500">Coins</div>
              </div>
              <div>
                <div className="font-bold text-purple-600">+{xp} ⭐</div>
                <div className="text-xs text-gray-500">XP</div>
              </div>
            </div>
          </div>

          <button
            onClick={onComplete}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-bold hover:opacity-90 transition-opacity"
          >
            Return to Pharmacy
          </button>
        </div>
      </div>
    );
  }

  // ── Teaching step ─────────────────────────────────────────────────────────
  if (step?.type === "teach") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <button onClick={onComplete} className="text-sm text-gray-400 hover:text-gray-700">← Leave</button>
          <div className="text-center">
            <div className="font-bold text-gray-800 text-sm">{module.emoji} {module.title}</div>
            <div className="text-xs text-gray-400">{module.npc}</div>
          </div>
          <span className="text-xs text-gray-400">{stepIdx + 1}/{module.steps.length}</span>
        </div>

        <ProgressBar />

        <div className="flex-1 overflow-y-auto px-4 pb-6 pt-4">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-12 h-12 rounded-full bg-teal-100 border-2 border-teal-400 flex items-center justify-center text-xl flex-shrink-0">
              👩‍⚕️
            </div>
            <div className="bg-teal-600 text-white rounded-2xl rounded-tl-none px-4 py-2 text-sm font-medium max-w-xs">
              {step.heading}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-5 mb-5">
            <h3 className="font-bold text-gray-900 text-base mb-3">{step.heading}</h3>
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {step.text}
            </div>
          </div>

          <button
            onClick={advance}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-bold hover:opacity-90 transition-opacity"
          >
            {isLast ? "Finish →" : "Continue →"}
          </button>
        </div>
      </div>
    );
  }

  // ── Question step ─────────────────────────────────────────────────────────
  if (step?.type === "question") {
    const correctOption = step.options.find(o => o.correct);
    const isCorrect     = selected === correctOption.id;

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <button onClick={onComplete} className="text-sm text-gray-400 hover:text-gray-700">← Leave</button>
          <div className="text-center">
            <div className="font-bold text-gray-800 text-sm">{module.emoji} {module.title}</div>
            <div className="text-xs text-gray-400">Question</div>
          </div>
          <span className="text-xs text-gray-400">{stepIdx + 1}/{module.steps.length}</span>
        </div>

        <ProgressBar />

        <div className="flex-1 overflow-y-auto px-4 pb-6 pt-4">
          <div className="bg-white rounded-2xl shadow-md p-5 mb-4">
            <div className="text-xs font-bold text-teal-600 uppercase tracking-wide mb-2">❓ Question</div>
            <p className="text-sm font-semibold text-gray-900 leading-snug">{step.question}</p>
          </div>

          <div className="space-y-2 mb-4">
            {step.options.map(opt => {
              let cls = "bg-white border-gray-200 text-gray-800";
              let icon = null;

              if (answered) {
                if (opt.correct) {
                  cls = "bg-green-100 border-green-400 text-green-900";
                  icon = <span className="text-green-600 mr-2">✓</span>;
                } else if (opt.id === selected && !opt.correct) {
                  cls = "bg-red-100 border-red-400 text-red-900";
                  icon = <span className="text-red-600 mr-2">✗</span>;
                } else {
                  cls = "bg-gray-50 border-gray-200 text-gray-400";
                }
              } else if (selected === opt.id) {
                cls = "bg-teal-100 border-teal-400 text-teal-900";
              }

              return (
                <button
                  key={opt.id}
                  onClick={() => !answered && setSelected(opt.id)}
                  disabled={answered}
                  className={`w-full text-left border-2 rounded-xl px-4 py-3 text-sm transition-all ${cls} ${!answered ? "hover:border-teal-400 cursor-pointer" : "cursor-default"}`}
                >
                  {icon}{opt.text}
                </button>
              );
            })}
          </div>

          {answered && (
            <div className={`rounded-2xl p-4 mb-4 ${isCorrect ? "bg-green-50 border border-green-200" : "bg-orange-50 border border-orange-200"}`}>
              <div className={`font-bold text-sm mb-1 ${isCorrect ? "text-green-700" : "text-orange-700"}`}>
                {isCorrect ? "✓ Correct!" : "Not quite — here's why:"}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{step.explanation}</p>
            </div>
          )}

          {!answered ? (
            <button
              onClick={submitAnswer}
              disabled={!selected}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-bold disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              Submit answer
            </button>
          ) : (
            <button
              onClick={advance}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-bold hover:opacity-90 transition-opacity"
            >
              {isLast ? "See results →" : "Continue →"}
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default PainTeachingGame;
